import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { type ActiveGamesAndPacksResponse, type ActivatedPacksResponse, type Store as StoreType } from '../../../../services/authService';
import auditService from '../../../../services/auditService';
import gamesService from '../../../../services/gamesService';
import type {
  CreateAuditStoreContext,
  CreateAuditValidationState,
  FormValues,
  InitialValues,
} from '../../../../types/auditFormTypes';
import type { Game } from '../../../../types/features/games';
import {
  processAuditDataManual,
  processPreviousAuditDataForCreateAudit,
  type ProcessedAuditTicket,
} from '../../../../utils/auditDataProcessor';
import { fetchGamesAndUpdateData } from '../../../../utils/fixOcrDataProcessors';
import showToast from '../../../../utils/toastUtils';
import {
  buildCreateAuditGamesMap,
  buildCreateAuditInitialValues,
  buildCreateAuditLookupDateTime,
  buildCreateAuditRetailerContext,
  buildCreateManualAuditPayload,
  normalizeCreateAuditPreviousTickets,
  validateCreateAuditFormState,
} from '../utils/createAuditPageUtils';

const useCreateAuditPageLogic = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreType[]>([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [shiftDate, setShiftDate] = useState('');
  const [shiftStartTime, setShiftStartTime] = useState('');
  const [shiftEndTime, setShiftEndTime] = useState('');
  const [fetchingPrevious, setFetchingPrevious] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previousAuditCount, setPreviousAuditCount] = useState(0);
  const [formData, setFormData] = useState<InitialValues | null>(null);
  const [gamesMapState, setGamesMapState] = useState<Map<string, number>>(new Map());
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [activeGamesAndPacks, setActiveGamesAndPacks] = useState<ActiveGamesAndPacksResponse>([]);
  const lastValidatedFormStateKeyRef = useRef('');
  const retailerContextRef = useRef({
    retailerId: '',
    retailerFromStore: false,
    barcodeFormat: '',
    direction: '' as '' | 'backwards' | 'forwards',
  });
  const gamesResponseCacheRef = useRef<Game[] | null>(null);
  const storeContextCacheRef = useRef(new Map<string, CreateAuditStoreContext>());

  const selectedStoreData = useMemo(
    () => stores.find((store) => store.id === selectedStore),
    [selectedStore, stores],
  );

  const commitValidationState = useCallback((validationState: CreateAuditValidationState): InitialValues => {
    lastValidatedFormStateKeyRef.current = validationState.stateKey;
    return validationState.formData;
  }, []);

  const getValidationState = useCallback((candidateFormData: InitialValues): CreateAuditValidationState => {
    return validateCreateAuditFormState(candidateFormData, activeGamesAndPacks);
  }, [activeGamesAndPacks]);

  const loadStores = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authService.getStores();
      const nextStores = response.stores || [];
      setStores(nextStores);

      if (nextStores.length > 0) {
        setSelectedStore(nextStores[0].id);
      }
    } catch (loadError) {
      console.error('Failed to load stores for create audit:', loadError);
      setError('Failed to load stores. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const getGamesResponse = useCallback(async () => {
    if (gamesResponseCacheRef.current) {
      return gamesResponseCacheRef.current;
    }

    try {
      const gamesResponse = await gamesService.getAllGames();
      gamesResponseCacheRef.current = gamesResponse;
      return gamesResponse;
    } catch (gamesError) {
      console.error('Failed to fetch games for create audit:', gamesError);
      showToast.warning('Failed to fetch game data. Totals may be limited.');
      return [];
    }
  }, []);

  const getStoreContext = useCallback(async (storeId: string) => {
    const cachedContext = storeContextCacheRef.current.get(storeId);

    if (cachedContext) {
      return cachedContext;
    }

    const [storeDetails, nextActiveGamesAndPacks, activatedPacks] = await Promise.all([
      authService.getStoreDetails(storeId),
      authService.getActiveGamesAndPacks(storeId).catch((activeGamesError) => {
        console.error('Failed to fetch active games and packs for create audit:', activeGamesError);
        showToast.warning('Failed to validate active games and packs. You can still continue.');
        return [] as ActiveGamesAndPacksResponse;
      }),
      authService.getActivatedPacks(storeId).catch((activatedPacksError) => {
        console.error('Failed to fetch activated packs metadata for create audit:', activatedPacksError);
        showToast.warning('Failed to load barcode format metadata. Ticket numbers may be formatted incorrectly.');
        return { items: [], total: 0 } as ActivatedPacksResponse;
      }),
    ]);

    const nextContext = {
      ...buildCreateAuditRetailerContext(storeDetails),
      activeGamesAndPacks: nextActiveGamesAndPacks,
      barcodeFormat: activatedPacks?.barcode_format,
      direction: activatedPacks?.direction,
    };

    storeContextCacheRef.current.set(storeId, nextContext);

    return nextContext;
  }, []);

  const handleContinue = useCallback(async () => {
    if (!selectedStore || !shiftDate || !shiftStartTime || !shiftEndTime) {
      showToast.error('Please fill in store, shift date, start time, and end time');
      return;
    }

    try {
      setFetchingPrevious(true);
      setError('');

      const [gamesResponse, storeContext] = await Promise.all([
        getGamesResponse(),
        getStoreContext(selectedStore),
      ]);

      const {
        retailerId,
        retailerFromStore,
        activeGamesAndPacks: nextActiveGamesAndPacks,
      } = storeContext;
      retailerContextRef.current = {
        retailerId,
        retailerFromStore,
        barcodeFormat: storeContext?.barcodeFormat || '',
        direction: storeContext?.direction || '',
      };
      const lookupDateTime = buildCreateAuditLookupDateTime(shiftDate, shiftEndTime);
      let processedPreviousTickets: ProcessedAuditTicket[] = [];
      let nextPreviousAuditCount = 0;

      if (lookupDateTime) {
        try {
          const previousData = await auditService.getMostRecentAuditDetails(selectedStore, lookupDateTime);
          nextPreviousAuditCount = previousData?.details?.length || 0;

          if (nextPreviousAuditCount > 0) {
            processedPreviousTickets = processPreviousAuditDataForCreateAudit(
              processAuditDataManual([], previousData.details || []),
            );
            showToast.success(`Loaded ${nextPreviousAuditCount} previous audit records`);
          } else {
            showToast.info('No previous audit data found for this shift');
          }
        } catch (previousAuditError) {
          console.log('No previous audit data found for create audit:', previousAuditError);
          showToast.info('No previous audit data found for this shift');
        }
      }

      const normalizedPreviousTickets = normalizeCreateAuditPreviousTickets(processedPreviousTickets);
      const baseTicketDetails = normalizedPreviousTickets.length > 0
        ? normalizedPreviousTickets
        : undefined;

      const { updatedTicketDetails } = await fetchGamesAndUpdateData(baseTicketDetails || [], gamesResponse);
      const nextFormData = buildCreateAuditInitialValues({
        retailerId,
        retailerFromStore,
        shiftDate,
        shiftStartTime,
        shiftEndTime,
        ticketDetails: updatedTicketDetails,
      });
      const validationState = validateCreateAuditFormState(nextFormData, nextActiveGamesAndPacks);

      setActiveGamesAndPacks(nextActiveGamesAndPacks);
      setGamesMapState(buildCreateAuditGamesMap(gamesResponse));
      setPreviousAuditCount(nextPreviousAuditCount);
      setHoveredRowIndex(null);
      setFormData(commitValidationState(validationState));
      setShowForm(true);
    } catch (continueError) {
      console.error('Error preparing create audit form:', continueError);
      setError('Failed to prepare audit form. Please try again.');
      showToast.error('Failed to prepare audit form. Please try again.');
    } finally {
      setFetchingPrevious(false);
    }
  }, [commitValidationState, getGamesResponse, getStoreContext, selectedStore, shiftDate, shiftEndTime, shiftStartTime]);

  const handleFormDataUpdate = useCallback((updatedFormData: InitialValues) => {
    setFormData((previousFormData) => {
      const mergedFormData = {
        ...(previousFormData || {}),
        ...updatedFormData,
      };

      if (retailerContextRef.current.retailerFromStore && !mergedFormData.retailer_id) {
        mergedFormData.retailer_id = retailerContextRef.current.retailerId;
        mergedFormData.retailer_from_store = true;
      }

      const validationState = getValidationState(mergedFormData);

      if (lastValidatedFormStateKeyRef.current === validationState.stateKey) {
        return previousFormData;
      }

      return commitValidationState(validationState);
    });
  }, [commitValidationState, getValidationState]);

  const handleSubmit = useCallback(async (data: FormValues) => {
    const validationState = getValidationState(data);

    if (validationState.invalidGames.length > 0 || validationState.invalidPacks.length > 0) {
      setFormData(commitValidationState(validationState));
      showToast.error('Please fix invalid game or pack entries before creating the audit.');
      return;
    }

    try {
      setSaving(true);
      const response = await auditService.createManualAudit(
        selectedStore,
        buildCreateManualAuditPayload(validationState.formData, {
          barcodeFormat: retailerContextRef.current.barcodeFormat || undefined,
        }),
      );

      if (response.success) {
        showToast.success('Audit created successfully!');
        navigate('/dashboard/audit');
        return;
      }

      showToast.warning(response?.message || 'Audit creation response received.');
    } catch (submitError: unknown) {
      const errorMessage = submitError instanceof Error ? submitError.message : 'Unknown error';
      showToast.error(`Failed to create audit: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  }, [commitValidationState, getValidationState, navigate, selectedStore]);

  const handleBackToSetup = useCallback(() => {
    setShowForm(false);
  }, []);

  const handleBackToAuditList = useCallback(() => {
    navigate('/dashboard/audit');
  }, [navigate]);

  return {
    loading,
    error,
    stores,
    selectedStore,
    selectedStoreData,
    shiftDate,
    shiftStartTime,
    shiftEndTime,
    showForm,
    fetchingPrevious,
    saving,
    previousAuditCount,
    formData,
    gamesMapState,
    hoveredRowIndex,
    handleStoreChange: setSelectedStore,
    handleShiftDateChange: setShiftDate,
    handleShiftStartTimeChange: setShiftStartTime,
    handleShiftEndTimeChange: setShiftEndTime,
    handleContinue,
    handleSubmit,
    handleFormDataUpdate,
    handleBackToSetup,
    handleBackToAuditList,
    handleRowHover: setHoveredRowIndex,
  };
};

export default useCreateAuditPageLogic;
